import { useState } from "react";

export interface BackupCodesPanelProps {
  codes:     string[];
  onClose?:  () => void;
  /** Title shown above the code grid. */
  title?:    string;
  /** Whether to show the "I saved them" confirmation button. */
  showAck?:  boolean;
}

/**
 * One-time-view panel showing 10 backup recovery codes.
 *
 * Codes are returned by the backend exactly once — when the user first
 * enables TOTP (or regenerates the set). The UX goal is: make it cheap to
 * copy/download/print, hard to dismiss accidentally, and obvious that
 * they will not be shown again.
 */
export function BackupCodesPanel({
  codes,
  onClose,
  title = "Резервные коды",
  showAck = true,
}: BackupCodesPanelProps) {
  const [copied, setCopied] = useState(false);
  const text = codes.join("\n");

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard blocked — show manual copy fallback */
    }
  };

  const download = () => {
    const blob = new Blob(
      [
        "FlameVPN — Резервные коды двухфакторной аутентификации\n",
        `Сгенерированы: ${new Date().toLocaleString("ru-RU")}\n`,
        "Каждый код можно использовать только один раз.\n",
        "Храните в надёжном месте отдельно от устройства с TOTP-приложением.\n\n",
        text,
        "\n",
      ],
      { type: "text/plain;charset=utf-8" },
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "flamevpn-backup-codes.txt";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="rounded-lg border border-warning/40 bg-[var(--color-warning-muted)]/30 p-5">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-fg">{title}</h3>
          <p className="mt-1 text-xs text-fg-muted">
            Сохраните эти коды. Каждый можно использовать <b>только один раз</b>,
            если потеряешь доступ к приложению TOTP. Они показываются <b>только сейчас</b>.
          </p>
        </div>
      </div>

      <div className="mb-4 grid grid-cols-2 gap-1.5 rounded-md border border-border bg-bg p-3 font-mono text-sm">
        {codes.map((c, i) => (
          <div
            key={c}
            className="flex items-center gap-2 rounded px-2 py-1 tabular-nums text-fg hover:bg-surface"
          >
            <span className="text-fg-subtle">{(i + 1).toString().padStart(2, "0")}.</span>
            <span>{c}</span>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => void copy()}
          className="rounded-md border border-border bg-surface px-3 py-1.5 text-xs text-fg hover:bg-[var(--color-surface-elevated)]"
        >
          {copied ? "Скопировано ✓" : "Скопировать все"}
        </button>
        <button
          type="button"
          onClick={download}
          className="rounded-md border border-border bg-surface px-3 py-1.5 text-xs text-fg hover:bg-[var(--color-surface-elevated)]"
        >
          Скачать .txt
        </button>
        {showAck && onClose && (
          <button
            type="button"
            onClick={onClose}
            className="ml-auto rounded-md bg-accent px-3 py-1.5 text-xs font-medium text-white hover:bg-[var(--color-accent-hover)]"
          >
            Сохранил, продолжить
          </button>
        )}
      </div>
    </div>
  );
}
